import qs from 'qs'
import { useLocation } from 'react-router-dom'

import { HandbookSection, Link, NewBoard, NewDatabase, PlusCircled } from 'almanac-ui/icons'
import { UploadCloud } from 'almanac-ui/icons/16/UploadCloud'
import { Campaign } from 'almanac-ui/icons/28'
import { AI } from 'almanac-ui/icons'
import { useModals } from 'almanac-ui/Modal'
import { ReactComponent as AddDocumentIcon } from 'assets/icons/common/newDoc.svg'
import { ReactComponent as AddFolderIcon } from 'assets/icons/common/newFolder.svg'
import cx from 'classnames'
import { useAuth } from 'hooks/UseAuth'
import { useDebounce } from 'hooks/useDebounce'
import { ObjectsToLoad } from 'proseShared/prompts/MentionPrompt/MentionPrompt'
import { useArticles } from 'proseShared/prompts/queries/useArticles'
import { useAlgoliaClient } from 'providers/AlgoliaClientProvider'
import { useEffect } from 'react'
import { AlmanacFile, MentionableArticle, SetState } from 'types'
import Styles from './CreateInlineMenu.module.scss'
import { InlineOption } from './InlineOption'
import { NewInlineOption } from './types'
import { useIsBlaze } from 'hooks/useIsBlaze'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { NewProjectModal } from 'components/BlazeCommandLine/modals/NewProjectModal'
import { FileIcon } from 'screens/Finder/components/FileIcon/FileIcon'
import { Wizards } from 'components/Wizards/Wizards'
import { TurnIntoWizard } from 'components/Wizards/TurnInto/TurnIntoWizard'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'

const DebounceInterval = 250

interface CreateInlineMenuProps {
  chosenOption: NewInlineOption | string
  close: () => void
  dropdownStyle?: string
  inputValue: string
  isProcessing: boolean
  onCreateDoc: (title: string) => void
  onUploadMediaFile?: () => void
  onCreateFolder: (title: string) => void
  onCreateUrlLink?: () => void
  onCreateSection?: () => void
  onCreateSuggestedDoc: (id: string, title: string) => void
  onOpenMore?: (title: string) => void
  overrideWidth?: number | undefined
  listItems: (NewInlineOption | string)[]
  setChosenOption: SetState<NewInlineOption | string>
  updateListItems: (articleKeys: string[]) => void
  suggestedDocs: MentionableArticle[]
  setSuggestedDocs: SetState<MentionableArticle[]>
  showPasteText: boolean
  showSuggestions: boolean
}

export const CreateInlineMenu = ({
  dropdownStyle,
  onCreateDoc,
  onUploadMediaFile,
  onCreateFolder,
  onCreateSection,
  onCreateUrlLink,
  onCreateSuggestedDoc,
  onOpenMore,
  isProcessing,
  overrideWidth,
  listItems,
  close,
  inputValue,
  chosenOption,
  setChosenOption,
  updateListItems,
  suggestedDocs,
  setSuggestedDocs,
  showPasteText,
  showSuggestions,
}: CreateInlineMenuProps) => {
  const auth = useAuth()
  const location = useLocation()
  const { currentFolder } = useCurrentFolderStore()
  const params = qs.parse(location.search, { ignoreQueryPrefix: true })
  const userId = params.userId
  const showingDefaults = listItems.includes(NewInlineOption.Doc) // doc is always shown, so if it is in the list items, we are showing the defaults
  const algoliaClient = useAlgoliaClient()
  const debouncedSearchTerm = useDebounce(inputValue, DebounceInterval)
  const { openModal } = useModals()
  const isBlaze = useIsBlaze()

  const { data: brandPlan } = useCurrentBrandPlan()

  const articlesQuery = useArticles({
    algoliaClient,
    auth,
    userId,
    numberOfHits: 10,
    editableDocsOnly: true,
    objectsToLoad: [ObjectsToLoad.Articles],
    query: debouncedSearchTerm,
  })

  useEffect(() => {
    document.getElementById(chosenOption)?.scrollIntoViewIfNeeded?.()
  }, [chosenOption])

  useEffect(() => {
    if (!articlesQuery?.data) return

    if (!showSuggestions) return

    setSuggestedDocs(articlesQuery.data)
  }, [articlesQuery?.data, setSuggestedDocs, showSuggestions])

  useEffect(() => {
    updateListItems(suggestedDocs.map((d) => d.key))
  }, [suggestedDocs, updateListItems])

  if (listItems.length === 0) return null

  return (
    <div className={cx(Styles.dropdown, dropdownStyle)} style={{ width: overrideWidth }} role="list">
      {listItems.includes(NewInlineOption.Doc) && (
        <InlineOption
          icon={<AddDocumentIcon />}
          onCreate={() => onCreateDoc(inputValue)}
          active={chosenOption === NewInlineOption.Doc}
          inputValue={inputValue}
          isProcessing={isProcessing}
          option={NewInlineOption.Doc}
          optionName="Blank Doc"
          overrideWidth={overrideWidth}
          setChosenOption={setChosenOption}
          showText
        />
      )}
      {listItems.includes(NewInlineOption.Database) && (
        <InlineOption
          icon={<NewDatabase />}
          onCreate={() => onCreateFolder(inputValue)}
          active={chosenOption === NewInlineOption.Database}
          inputValue={inputValue}
          isProcessing={isProcessing}
          option={NewInlineOption.Database}
          optionName={`New ${isBlaze ? 'Project' : 'Database'}`}
          overrideWidth={overrideWidth}
          setChosenOption={setChosenOption}
          showText
        />
      )}
      {listItems.includes(NewInlineOption.Handbook) && (
        <InlineOption
          icon={<AddFolderIcon />}
          onCreate={() => onCreateFolder(inputValue)}
          active={chosenOption === NewInlineOption.Handbook}
          inputValue={inputValue}
          isProcessing={isProcessing}
          option={NewInlineOption.Handbook}
          optionName="New Folder"
          overrideWidth={overrideWidth}
          setChosenOption={setChosenOption}
          showText
        />
      )}
      {listItems.includes(NewInlineOption.GeneratedDoc) && (
        <InlineOption
          icon={<AI />}
          onCreate={() => {
            openModal(Wizards, {
              initialValues: { project: currentFolder },
            })
            close()
          }}
          active={chosenOption === NewInlineOption.GeneratedDoc}
          inputValue={inputValue}
          isProcessing={isProcessing}
          option={NewInlineOption.GeneratedDoc}
          id="generate-doc-with-ai"
          optionName="Generated Doc"
          overrideWidth={overrideWidth}
          setChosenOption={setChosenOption}
          showText={false}
        />
      )}
      {listItems.includes(NewInlineOption.Campaign) && (
        <InlineOption
          icon={<Campaign size={16} />}
          onCreate={() => {
            openModal(TurnIntoWizard, {
              initialIsCampaign: true,
              initialValues: { project: currentFolder || undefined },
            })

            close()
          }}
          active={chosenOption === NewInlineOption.Campaign}
          inputValue={inputValue}
          isProcessing={isProcessing}
          option={NewInlineOption.Campaign}
          optionName="Create Campaign"
          overrideWidth={overrideWidth}
          setChosenOption={setChosenOption}
          showText={false}
        />
      )}
      {listItems.includes(NewInlineOption.MediaFile) && (
        <InlineOption
          icon={<UploadCloud color="currentColor" />}
          onCreate={() => {
            onUploadMediaFile?.()
            close()
          }}
          active={chosenOption === NewInlineOption.MediaFile}
          inputValue={inputValue}
          isProcessing={isProcessing}
          option={NewInlineOption.MediaFile}
          optionName="Upload File"
          overrideWidth={overrideWidth}
          setChosenOption={setChosenOption}
          showText={false}
        />
      )}
      {listItems.includes(NewInlineOption.BoardView) && (
        <InlineOption
          icon={<NewBoard />}
          onCreate={() => onCreateFolder(inputValue)}
          active={chosenOption === NewInlineOption.BoardView}
          inputValue={inputValue}
          isProcessing={isProcessing}
          option={NewInlineOption.BoardView}
          optionName="New Board"
          overrideWidth={overrideWidth}
          setChosenOption={setChosenOption}
          showText
        />
      )}
      {listItems.includes(NewInlineOption.Project) && (
        <InlineOption
          icon={<NewDatabase />}
          onCreate={() => {
            close()
            openModal(NewProjectModal, { parentFolder: currentFolder })
          }}
          active={chosenOption === NewInlineOption.BoardView}
          inputValue={inputValue}
          isProcessing={isProcessing}
          option={NewInlineOption.Project}
          optionName="New Project"
          overrideWidth={overrideWidth}
          setChosenOption={setChosenOption}
          showText
        />
      )}
      {listItems.includes(NewInlineOption.More) && (
        <InlineOption
          icon={<PlusCircled />}
          onCreate={() => {
            onOpenMore?.(inputValue)
            close()
          }}
          active={chosenOption === NewInlineOption.More}
          inputValue={inputValue}
          isProcessing={isProcessing}
          option={NewInlineOption.More}
          optionName="More Options..."
          overrideWidth={overrideWidth}
          setChosenOption={setChosenOption}
          showText={false}
        />
      )}
      {(listItems.includes(NewInlineOption.Section) || listItems.includes(NewInlineOption.UrlLink)) && (
        <div className={Styles.section}>
          {listItems.includes(NewInlineOption.UrlLink) && (
            <InlineOption
              icon={<Link />}
              onCreate={() => onCreateUrlLink?.()}
              active={chosenOption === NewInlineOption.UrlLink}
              inputValue={inputValue}
              isProcessing={isProcessing}
              option={NewInlineOption.UrlLink}
              optionName="New URL Link"
              overrideWidth={overrideWidth}
              setChosenOption={setChosenOption}
              showText={false}
            />
          )}
          {listItems.includes(NewInlineOption.Section) && (
            <InlineOption
              icon={<HandbookSection />}
              onCreate={() => onCreateSection?.()}
              active={chosenOption === NewInlineOption.Section}
              inputValue={inputValue}
              isProcessing={isProcessing}
              option={NewInlineOption.Section}
              optionName="New Section Header"
              overrideWidth={overrideWidth}
              setChosenOption={setChosenOption}
              showText={false}
            />
          )}
        </div>
      )}

      {!!inputValue.length && !!suggestedDocs.length && (
        <div className={cx({ [Styles.section]: showingDefaults })}>
          <div className={Styles.headerText}>Add a mentioned doc</div>
          {suggestedDocs.map((suggestedDoc) => (
            <InlineOption
              key={suggestedDoc.key}
              icon={<FileIcon file={suggestedDoc as unknown as AlmanacFile} brandPlan={brandPlan} />}
              onCreate={() => onCreateSuggestedDoc(suggestedDoc.key, suggestedDoc.title)}
              active={chosenOption === suggestedDoc.key}
              inputValue={inputValue}
              isProcessing={isProcessing}
              option={suggestedDoc.key}
              optionName={suggestedDoc.title}
              overrideWidth={overrideWidth}
              setChosenOption={setChosenOption}
              showText={false}
            />
          ))}
          {showPasteText && <div className={Styles.mentionsHelpText}>Paste URL to mention doc</div>}
        </div>
      )}
    </div>
  )
}
